/**
 * Cross-firm Firm Tabs list (staff portal). Flat — every kind sits in one
 * table with a "Type" column and a Type filter in the toolbar. Mirrors the
 * Lawyer Profiles / Articles list pages visually.
 *
 * Replaces the previous 2-row grid of kind-cards (deleted alongside this
 * change). The legacy `/profiles/firm-tabs/:kind` route still resolves —
 * `KindRedirect` turns it into `/profiles/firm-tabs?type=<kind>` so old links
 * keep working.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Select, type Column } from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import {
  FirmChip,
  ProfilesFilterCell,
  ProfilesListFilters,
  RegionNodeChip,
} from "@/components/Profiles/ListFilters";
import { parseRegionKind } from "@/hooks/useRegions";
import { ROUTES } from "@/routes";
import {
  FIRM_TAB_KINDS,
  firmTabsService,
  isFirmTabKind,
  type FirmTabKind,
} from "@/api/profiles/firmTabsService";
import type { FirmTab } from "@/api/profiles/types";
import styles from "../ProfilesList.module.css";

const KIND_LABELS: Record<FirmTabKind, string> = {
  client_testimonials: "Client testimonials",
  diversity: "Diversity & Inclusion",
};

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  ...FIRM_TAB_KINDS.map(kind => ({ value: kind, label: KIND_LABELS[kind] })),
];

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function FirmTabsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const companyId = searchParams.get("companyId") ?? undefined;
  const status = searchParams.get("status") ?? undefined;
  const q = searchParams.get("q") ?? undefined;
  const regionKind = parseRegionKind(searchParams.get("regionKind"));
  const regionId = searchParams.get("regionId") ?? undefined;
  const typeParam = searchParams.get("type") ?? "";
  const kind: FirmTabKind | undefined = isFirmTabKind(typeParam) ? typeParam : undefined;

  const [deleteTarget, setDeleteTarget] = useState<FirmTab | null>(null);

  const filters = useMemo(
    () => ({ companyId, status, q, regionKind, regionId, kind }),
    [companyId, status, q, regionKind, regionId, kind],
  );

  const listQuery = useQuery({
    // Keep cache distinct per filter set.
    queryKey: ["profiles", "firm-tabs", "list", filters] as const,
    queryFn: () => firmTabsService.list(filters),
  });

  // The flat (no-kind) list calls `firmTabsService.list` with no `kind`, which
  // fans out across every per-kind endpoint in parallel and concatenates the rows.
  // For the action column we need each row's kind — derived from `tabType`.
  // `tabType` is the API enum (snake_case) and matches FirmTabKind exactly for
  // the staff list view, so we cast narrowly.
  function kindForRow(row: FirmTab): FirmTabKind {
    return (isFirmTabKind(row.tabType) ? row.tabType : "client_testimonials") as FirmTabKind;
  }

  const deleteMutation = useMutation({
    mutationFn: async (target: { kind: FirmTabKind; id: string }) =>
      firmTabsService.remove(target.kind, target.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["profiles", "firm-tabs"] });
      setDeleteTarget(null);
    },
  });

  const rows = listQuery.data?.data ?? [];

  // Newest-updated first.
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1)),
    [rows],
  );

  // "New firm tab" target: if a Type filter is selected, deep-link to the
  // new-form for that kind; otherwise default to "client_testimonials". We surface the
  // active kind in the button label so admins know what they'll get.
  const newKind: FirmTabKind = kind ?? "client_testimonials";
  const newLabel = kind ? `New ${KIND_LABELS[kind].toLowerCase()} tab` : "New firm tab";

  // Columns for the shared <DataTable>. Columns that don't map to a single
  // field (Type, Regions, Actions) use a valid `keyof FirmTab` accessor purely
  // to satisfy the type and supply a `render`. The Regions column opts into
  // `wrap` so the region chips can flow onto multiple lines.
  const columns: Column<FirmTab>[] = [
    {
      header: "Firm",
      accessor: "companyId",
      render: (_value, row) => <FirmChip companyId={row.companyId} />,
    },
    {
      header: "Title",
      accessor: "title",
      render: (_value, row) => {
        const rowKind = kindForRow(row);
        const title = row.title || "(untitled)";
        return (
          <span data-testid={`firm-tab-row-${row.id}`}>
            <Link to={ROUTES.profiles.firmTabDetail(rowKind, row.id)} className={styles.titleLink}>
              {title}
            </Link>
          </span>
        );
      },
    },
    {
      header: "Type",
      accessor: "tabType",
      render: (_value, row) => KIND_LABELS[kindForRow(row)],
    },
    {
      header: "Regions",
      accessor: "regions",
      wrap: true,
      render: (_value, row) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(row.regions ?? []).map(r => (
            <RegionNodeChip key={`${r.kind}:${r.id}`} region={r} />
          ))}
        </div>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => (
        <span className={styles.statusPill} data-status={row.status}>
          {row.status}
        </span>
      ),
    },
    {
      header: "Updated",
      accessor: "updatedAt",
      render: (_value, row) => <span className={styles.muted}>{formatDate(row.updatedAt)}</span>,
    },
    {
      header: "",
      accessor: "id",
      render: (_value, row) => {
        const rowKind = kindForRow(row);
        const title = row.title || "(untitled)";
        return (
          <div className={styles.actions}>
            <Button
              variant="outlined"
              size="small"
              aria-label={`View ${title}`}
              onClick={() => navigate(ROUTES.profiles.firmTabDetail(rowKind, row.id))}
            >
              View
            </Button>
            <Button
              variant="outlined"
              size="small"
              aria-label={`Edit ${title}`}
              onClick={() => navigate(ROUTES.profiles.firmTabDetail(rowKind, row.id))}
            >
              Edit
            </Button>
            <Button
              variant="text"
              color="error"
              size="small"
              aria-label={`Delete ${title}`}
              onClick={() => setDeleteTarget(row)}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Firm tabs">
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(ROUTES.profiles.firmTabNew(newKind))}
          data-testid="new-firm-tab-button"
        >
          {newLabel}
        </Button>
      </PageHeader>

      <ProfilesListFilters enabledFilters={{ company: true, status: true, q: true, region: true }}>
        <ProfilesFilterCell>
          <Select
            label="Type"
            value={typeParam}
            options={TYPE_OPTIONS}
            onChange={e =>
              setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                if (e.target.value) next.set("type", e.target.value);
                else next.delete("type");
                return next;
              })
            }
            size="small"
            aria-label="Type filter"
          />
        </ProfilesFilterCell>
      </ProfilesListFilters>

      <div className={styles.contentSection}>
        {listQuery.isError && (
          <div role="alert" className={styles.errorBanner}>
            {listQuery.error instanceof Error
              ? listQuery.error.message
              : "Failed to load firm tabs"}
          </div>
        )}

        {listQuery.isPending ? (
          <p role="status" className={styles.muted}>
            Loading firm tabs…
          </p>
        ) : sortedRows.length === 0 ? (
          <p data-testid="empty-state" className={styles.emptyState}>
            No firm tabs match the current filters.
          </p>
        ) : (
          <DataTable data={sortedRows} columns={columns} rowKey="id" />
        )}
      </div>

      {deleteTarget && (
        <ConfirmationModal
          title="Delete firm tab"
          message={`Delete '${deleteTarget.title || deleteTarget.id}'?`}
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync({
              kind: kindForRow(deleteTarget),
              id: deleteTarget.id,
            });
          }}
        />
      )}
    </div>
  );
}

export default FirmTabsList;
